import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button Component
 * 
 * Reusable button component with predefined variants based on theme colors.
 * Uses inline styles with CSS variables to ensure theme colors are applied correctly.
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  children,
  disabled = false,
  onClick,
  className = '',
  type = 'button',
  icon,
  style,
  isLoading = false,
  ...rest
}) => {
  // Base styles that work for all variants
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md';

  // Size variants
  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  // Variant styles using CSS variables
  const variantStyles = {
    primary: {
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-surface)',
      borderColor: 'var(--color-primary)',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    secondary: {
      backgroundColor: 'var(--color-secondary)',
      color: 'var(--color-surface)',
      borderColor: 'var(--color-secondary)',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    accent: {
      backgroundColor: 'var(--color-accent)',
      color: 'var(--color-secondary)',
      borderColor: 'var(--color-accent)',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    neutral: {
      backgroundColor: 'var(--color-neutral)',
      color: 'var(--color-secondary)',
      borderColor: 'var(--color-neutral)',
      borderWidth: '1px',
      borderStyle: 'solid',
    },
    outline: {
      backgroundColor: 'transparent',
      color: 'var(--color-primary)',
      borderColor: 'var(--color-primary)',
      borderWidth: '2px',
      borderStyle: 'solid',
    },
  };

  // Combine classes
  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${className}`;
  const combinedStyles = { ...variantStyles[variant], ...style };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={buttonClasses}
      style={combinedStyles}
      {...rest}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {!isLoading && icon && <span className="mr-2 flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'accent', 'neutral', 'outline']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  icon: PropTypes.node,
  isLoading: PropTypes.bool,
};

export default Button;
